// Bridges the BE-minted `conversationId` (UUIDv5 from
// /v1/conversations/request) and the MLS group id that the
// ping-openmls-sdk MessagingClient uses internally — and, on first
// creation, invites the peer so subsequent application messages can
// actually be decrypted on their device.
//
// Flow on first send for a fresh chat:
//  1. SDK mints a local MLS group via `client.createConversation()` —
//     only the sender is a member at this point.
//  2. We look up the peer's account_id from the local conversation row
//     (stored when NewChatModal called requestChat()).
//  3. We fetch the peer's device list + a fresh KeyPackage:
//       GET /v1/users/{peerAccountId}/devices
//       GET /v1/users/{peerAccountId}/key-package?device_id=<dev>
//  4. We prime the PingTransport with the recipient device id so the
//     next Welcome envelope the SDK emits carries `?recipient=` on
//     POST /v1/groups/{conv_id}/messages — the BE uses that to insert
//     the peer into group_members.
//  5. We call `conv.addMembers([keyPackage])`. The SDK generates a
//     Welcome (encrypted to the peer's KeyPackage) + Commit, and
//     auto-publishes both through the transport.
//  6. The caller then calls `conv.send(appBytes)` for the actual
//     message — the peer can decrypt because they're now a member
//     at the post-Commit epoch.
//
// Idempotency: the (conversationId → MLS group id) mapping is cached
// in a host-provided KVStore, and an "invited" flag prevents re-running
// the addMembers step on subsequent sends.
//
// This module was lifted from apps/web/src/features/chat/mlsConversation.ts
// to packages/ui so both apps/web (Dexie-backed) and apps/desktop
// (Zustand+KV) can share the orchestration. Storage access is
// injected through the `KVStore` + `ConversationStore` interfaces;
// network access is injected through `ApiClient`.

import type { ApiClient } from '../transport-mls/PingTransport';
import { base64urlToBytes, bytesToHex } from './mlsBytes';
import { consumePeerKeyPackage, fetchPeerDevices } from './keyPackages';

// Structural slice of a Logger so this module doesn't depend on the
// concrete `log` singleton on either platform. apps/web's `log` uses
// `(meta, msg?)` signatures; this matches that shape (and any other
// platform logger that exposes the same interface). Note: this is
// intentionally NOT packages/ui's own `Logger` from log/log.ts —
// that one's first arg is a string, while the chat callers thread
// apps/web's meta-first logger here.
export interface MlsLogger {
  debug(meta: Record<string, unknown>, msg?: string): void;
  info(meta: Record<string, unknown>, msg?: string): void;
  warn(meta: Record<string, unknown>, msg?: string): void;
  error(meta: Record<string, unknown>, msg?: string): void;
}

// Structural type instead of importing MessagingClient. The
// @ping/ui useSession hook surfaces a MessagingClient typed against
// the pnpm-hoisted copy, while a direct import here would resolve
// through a different node_modules path; TypeScript treats them as
// nominally distinct even though they're the same class at runtime.
// Pinning a structural shape sidesteps the duplicate-class-identity
// mismatch.
export interface MessagingClientLike {
  createConversation(opts: { name?: string }): Promise<MlsConversationLike>;
  getConversation(id: Uint8Array): MlsConversationLike;
  // Optional — used to verify a cached MLS id still corresponds to
  // a group the SDK actually knows about. After an identity wipe
  // the cache survives in storage but the SDK's in-memory HashMap is
  // empty; without this check `getConversation` returns a proxy
  // that fails on the first `send` with "unknown conversation".
  listConversations?(): Promise<Array<{ id: Uint8Array }>>;
  // Optional MLS roster read (ping-openmls-sdk `members()`): one entry per
  // (device) leaf, `user_id` = SHA-256(identity pubkey) =
  // `base64urlToBytes(accountId)`. Used to map an accountId → leaf index(es)
  // for member removal. Absent on bindings that predate the wrapper.
  members?(id: Uint8Array): Promise<ReadonlyArray<MlsMemberInfo>>;
}

// Mirrors ping-openmls-sdk `MemberInfo`. `base64url(user_id) === accountId`.
export interface MlsMemberInfo {
  readonly user_id: Uint8Array;
  readonly leaf_index: number;
}

// Matches the SDK's MessageEnvelope return on conv.send (see
// ping-openmls-sdk's `Conversation.send(plaintext): Promise<MessageEnvelope>`)
// to the extent that the chat surface uses it for local-echo. Other
// fields exist on the wire — keeping the shape narrow avoids tying
// us to the SDK's full type which lives in a different node_modules
// path under pnpm.
export interface SentEnvelopeLike {
  conversation_id: Uint8Array;
  epoch: number | bigint;
  content_hash: Uint8Array;
  hlc: { wall_ms: number | bigint; logical: number };
}

// Mirrors `KeyPackageEntry` in ping-openmls-sdk's types — the wasm
// SDK's serde deserializer requires BOTH fields and rejects bare
// `Uint8Array` entries with `missing field deviceId`. We pin the
// camelCase shape exactly; field names are part of the SDK's wire
// contract (CBOR struct serialization).
export interface MlsKeyPackageEntry {
  readonly deviceId: Uint8Array;
  readonly keyPackage: Uint8Array;
}

export interface MlsConversationLike {
  readonly id: Uint8Array;
  send(plaintext: Uint8Array): Promise<SentEnvelopeLike>;
  addMembers(entries: MlsKeyPackageEntry[]): Promise<void>;
  // Optional MLS Commit that evicts the given leaves (ping-openmls-sdk
  // `removeMembers`). Absent on bindings that predate the wrapper.
  removeMembers?(leafIndexes: number[]): Promise<void>;
  // Optional GroupContextExtensions Commit that changes the conversation `name`
  // (carries the `ping:meta:v1:` blob — title + avatar-media-id) in group state,
  // so a rename / avatar change propagates to every member via MLS rather than a
  // side broadcast. Absent on bindings predating ping-openmls-sdk 0.7.6.
  setConversationName?(name: string): Promise<void>;
}

// PingTransport-shaped hint sink. We don't import the class directly
// because @ping/ui re-exports it through a barrel; structurally typing
// avoids the duplicate-class-identity issue that bit us with
// MessagingClient.
export interface WelcomeRecipientSink {
  setWelcomeRecipient(conversationIdHex: string, recipientDeviceIdHex: string): void;
  /**
   * Multi-device variant — primes one Welcome envelope to fan out to
   * every joining device in a single `?recipient=A,B,C` query. When
   * available the invite path prefers this over [[setWelcomeRecipient]].
   */
  setWelcomeRecipients(
    conversationIdHex: string,
    recipientDeviceIdHexes: readonly string[],
  ): void;
}

// Platform-agnostic key-value store for caching the
// (beConversationId → MLS group bytes) mapping and the
// "peer-invited" flag. apps/web wires this to Dexie's
// `settings_kv` table; apps/desktop will wire it to its
// Zustand+KV slice.
export interface KVStore {
  get(key: string): Promise<{ value: unknown } | undefined>;
  put(record: { key: string; value: unknown; updatedAt: number }): Promise<void>;
  delete(key: string): Promise<void>;
}

export interface ConversationStoreRow {
  readonly kind: 'direct' | 'group';
  readonly peerAccountId?: string;
  readonly memberAccountIds?: readonly string[];
  readonly name?: string;
}

/** Row shape for group dedup scans (phase 1 web-group-create). */
export interface GroupConversationListRow {
  readonly id: string;
  readonly kind: 'direct' | 'group';
  readonly memberAccountIds?: readonly string[];
}

// Platform-agnostic accessor for the local conversation row. The
// invite step reads `kind` + `peerAccountId`; group create uses
// optional dedup hooks.
export interface ConversationStore {
  get(localId: string): Promise<ConversationStoreRow | undefined>;
  findGroupByPeerAccountIds?(
    peerAccountIds: readonly string[],
  ): Promise<string | undefined>;
  listGroupConversations?(): Promise<readonly GroupConversationListRow[]>;
}

const MLS_KEY_PREFIX = 'mls.conv.';
const INVITED_KEY_PREFIX = 'mls.invited.';

interface CachedMlsId {
  bytes: number[]; // serialized Uint8Array (IndexedDB doesn't structured-clone typed arrays cleanly across all backends)
}

/**
 * Thrown by `getOrCreateMlsConversation` when the local KV pointer
 * resolves to an MLS group_id the SDK doesn't recognise after init
 * rehydrate. The legacy code path here used to mint a fresh group,
 * but that lands a 404 on send (BE membership is keyed by the
 * ORIGINAL group_id) and the orphan-prune that follows permanently
 * deletes the local conversation row.
 *
 * Hosts that catch this should:
 *   - NOT call `markConversationOrphaned` / prune the local row
 *   - Surface a recoverable error to the user ("This chat couldn't
 *     be loaded — try reloading or start a new chat") so a retry
 *     after a fresh init (next page load) can pick it back up
 *     once the SDK rehydrates correctly.
 */
export class MlsStateUnrecoverableError extends Error {
  readonly beConversationId: string;
  constructor(beConversationId: string) {
    super(
      `MLS state for conversation ${beConversationId.slice(0, 8)} is not loaded `
        + `in the SDK after rehydrate. Refusing to mint a fresh group — that `
        + `would 404 on send and prune the chat. Reload or restart to retry.`,
    );
    this.name = 'MlsStateUnrecoverableError';
    this.beConversationId = beConversationId;
  }
}

export interface GetOrCreateOptions {
  readonly client: MessagingClientLike;
  readonly transport: WelcomeRecipientSink | null;
  readonly beConversationId: string;
  readonly conversationName: string | undefined;
  readonly log: MlsLogger;
  // Caller-supplied peer account_id. When provided, the invite step
  // uses this instead of trying to read it from the local conversation
  // row — necessary when `getOrCreateMlsConversation` is called BEFORE
  // the row has been written (e.g. NewChatModal.handlePick mints the
  // MLS group eagerly so it can use the resulting mlsIdHex as the
  // local row's primary key, but the row doesn't exist yet at that
  // moment). If omitted, the invite step falls back to the
  // ConversationStore lookup.
  readonly peerAccountId?: string;
  // Pre-claimed peer device + KeyPackage. When the caller has already
  // claimed a KP via `POST /v1/contacts/lookup` (atomic resolve +
  // claim), pass the result through here — the invite step uses
  // these bytes directly and skips the per-user devices/key-package
  // fan-out that drove the older flow. This is the canonical path
  // for `/v1/contacts/lookup`-driven new-chat creation.
  readonly preClaimedPeer?: {
    readonly deviceIdHex: string;
    readonly keyPackageBytes: Uint8Array;
  };
  /**
   * Multi-device variant of [[preClaimedPeer]] — one entry per
   * joining device of the peer. The MLS group becomes a 2N-leaf
   * group (sender's devices + N peer devices), and the single
   * Welcome envelope produced by `addMembers` is addressed to all
   * peer devices via `POST /v1/messages?recipient=A,B,C`. Per
   * docs/architecture/multi-device.md "Per-device fan-out".
   *
   * When both `preClaimedPeer` and `preClaimedPeers` are supplied,
   * `preClaimedPeers` wins.
   */
  readonly preClaimedPeers?: ReadonlyArray<{
    readonly deviceIdHex: string;
    readonly keyPackageBytes: Uint8Array;
  }>;
  /**
   * Pre-claimed devices of the LOCAL account's OTHER devices (linked
   * siblings, excluding this device). Added to the group in the SAME
   * addMembers Commit as the peer so a brand-new 1:1 appears on every one
   * of the creator's devices in realtime — web parity with iOS's
   * `claimSelfSiblingDevices()`. Best-effort: the caller claims these on
   * its own account (minus its device id) and threads them through here.
   */
  readonly selfSiblingDevices?: ReadonlyArray<{
    readonly deviceIdHex: string;
    readonly keyPackageBytes: Uint8Array;
  }>;
  // Storage + network adapters. Lifted from the apps/web Dexie
  // singletons so packages/ui itself doesn't pin to either platform's
  // persistence layer.
  readonly kv: KVStore;
  readonly conversations: ConversationStore;
  readonly api: ApiClient;
}

export type InviteStatus =
  | { kind: 'ok' }
  | { kind: 'skipped'; reason: 'already_invited' | 'no_peer_account' | 'group_chat' }
  | {
      kind: 'failed';
      reason: 'peer_pool_empty' | 'no_peer_devices' | 'no_transport' | 'other';
      detail?: string;
    };

export interface MlsConversationHandle {
  readonly conv: MlsConversationLike;
  readonly conversationIdHex: string;
  readonly inviteStatus: InviteStatus;
}

/**
 * Returns a Conversation proxy for the BE conversationId, minting a
 * fresh MLS group on first use AND inviting the peer in the same call
 * so the first application message that follows can be decrypted on
 * the peer's device. Subsequent calls just reuse the cached MLS
 * group id (no re-invite).
 */
export async function getOrCreateMlsConversation(
  opts: GetOrCreateOptions,
): Promise<MlsConversationHandle> {
  const {
    client,
    transport,
    beConversationId,
    conversationName,
    log,
    peerAccountId,
    preClaimedPeer,
    preClaimedPeers,
    selfSiblingDevices,
    kv,
    conversations,
    api,
  } = opts;
  const key = MLS_KEY_PREFIX + beConversationId;
  const existing = await kv.get(key);

  // `cacheStale` flips to true when we have a cached mlsId but the
  // SDK has no record of it (typical after an identity wipe). We
  // then skip the cache-hit branch and fall through to the mint
  // path further down, which writes clean cache entries.
  if (existing?.value) {
    const stored = existing.value as CachedMlsId;
    if (Array.isArray(stored.bytes) && stored.bytes.length > 0) {
      const mlsId = new Uint8Array(stored.bytes);
      if (typeof client.listConversations === 'function') {
        try {
          const known = await client.listConversations();
          const sdkHasIt = known.some(
            (k) => k.id.length === mlsId.length && sameBytes(k.id, mlsId),
          );
          if (!sdkHasIt) {
            // CRITICAL: do NOT fall through to mint-fresh.
            //
            // Mint-fresh creates a brand-new MLS group with a
            // different `group_id`. The send pipeline then puts
            // that NEW id into the envelope's `conversation_id`
            // field, but the BE's `auth.conversation_members`
            // table only has the ORIGINAL `group_id` for this
            // device. `is_active_member(new_id, device)` returns
            // false → POST /v1/messages → 404 → orphan-prune
            // deletes the local conversation row → the user's
            // chat is permanently gone, in a flow that started
            // with a recoverable SDK-side rehydrate miss.
            //
            // Far better outcome: throw a typed error here so the
            // host can show "this chat couldn't be loaded — start
            // a fresh chat with this person" and leave the local
            // row intact for the user to retry / debug. Reloading
            // sometimes recovers (SDK rehydrate is racy on first
            // boot); a destructive auto-mint never does.
            log.error({
              event: 'mlsConversation.cache_stale',
              beConversationId,
              cachedMlsIdPrefix: bytesToHex(mlsId).slice(0, 8),
              message:
                'SDK does not have this conversation in listConversations() '
                + 'after rehydrate. Refusing to mint a fresh MLS group — '
                + 'that would land a 404 on send and prune the chat. The host '
                + 'should surface a reload prompt to the user.',
            });
            throw new MlsStateUnrecoverableError(beConversationId);
          }
        } catch (err) {
          log.warn({
            event: 'mlsConversation.list_failed',
            error: (err as Error).message,
          });
        }
      }
      // SDK has the conversation — happy path. Get the
      // Conversation handle and resolve any pending invite work.
      try {
        const conv = client.getConversation(mlsId);
        const conversationIdHex = bytesToHex(mlsId);
        // Mirror the dual-key write from the mint-fresh branch below
        // (lines 369-380). Without this, a chat created via
        // `getOrCreateMlsConversation({beConversationId: 'acct:…'})`
        // only writes the `acct:` cache entry on the first call. A
        // later lookup by mlsIdHex (e.g. WebChatRoom.tsx rebinding
        // to the same chat on the next user message) misses the
        // cache, falls through to mint-fresh, creates a SECOND MLS
        // group with a different group_id, and every send to that
        // ghost group 404s on the BE because the device isn't a
        // member of it — `is_active_member` check in
        // `services/message-service/src/store/append.rs::append_in_tx`
        // line 154. The orphan-prune that follows then deletes the
        // local chat row entirely. Idempotent put — re-running on
        // every cache hit is safe (overwrites with the same bytes +
        // updates `updatedAt`).
        if (conversationIdHex !== beConversationId) {
          await kv.put({
            key: MLS_KEY_PREFIX + conversationIdHex,
            value: { bytes: Array.from(mlsId) },
            updatedAt: Date.now(),
          });
        }
        // Cover the reload-between-mint-and-invite case: the cached
        // MLS group id exists but `invitePeerIfNeeded` may not have
        // completed yet. The function is idempotent (no-ops once the
        // "invited" flag is set in the KVStore) so calling it here
        // every time is safe and cheap.
        const inviteStatus = await runInviteWithStatus({
          conv,
          transport,
          beConversationId,
          conversationIdHex,
          log,
          kv,
          conversations,
          api,
          ...(peerAccountId ? { peerAccountIdOverride: peerAccountId } : {}),
          ...(preClaimedPeer ? { preClaimedPeer } : {}),
          ...(preClaimedPeers ? { preClaimedPeers } : {}),
          ...(selfSiblingDevices ? { selfSiblingDevices } : {}),
        });
        return { conv, conversationIdHex, inviteStatus };
      } catch (err) {
        // The cache_stale branch above already threw for "SDK has
        // no record of this group". This catch covers a narrower
        // case: listConversations reported the id but
        // `getConversation` then throws — typically a corrupt
        // meta row. Same outcome: refuse to mint-fresh, surface
        // an unrecoverable error so the host can prompt the user
        // to reload / retry instead of silently pruning.
        log.error({
          event: 'mlsConversation.getConversation_failed',
          beConversationId,
          message: (err as Error).message,
        });
        throw new MlsStateUnrecoverableError(beConversationId);
      }
    }
  }

  const createOpts: { name?: string } =
    conversationName !== undefined && conversationName.length > 0
      ? { name: conversationName }
      : {};
  const fresh = await client.createConversation(createOpts);
  const conversationIdHex = bytesToHex(fresh.id);
  const mlsBytes = Array.from(fresh.id);

  // Write two cache entries pointing at the same MLS group:
  //   1. `mls.conv.<beConversationId>` — the BE UUIDv5 the inviter
  //      originally used; kept so legacy callers / dual-key code
  //      paths still resolve.
  //   2. `mls.conv.<mlsIdHex>` — the MLS group id itself; this is
  //      the canonical local key going forward (receiver side and
  //      post-NewChatModal flow on the inviter both use this).
  //      Without entry #2, a subsequent
  //      `getOrCreateMlsConversation({beConversationId: mlsIdHex})`
  //      would miss the cache and mint a fresh orphan group.
  await kv.put({
    key,
    value: { bytes: mlsBytes },
    updatedAt: Date.now(),
  });
  if (conversationIdHex !== beConversationId) {
    await kv.put({
      key: MLS_KEY_PREFIX + conversationIdHex,
      value: { bytes: mlsBytes },
      updatedAt: Date.now(),
    });
  }

  // Run the invite step before returning. Failure here is non-fatal:
  // the sender can still send-to-self for diagnostic purposes, but
  // we surface the result so the chat UI can show a "peer not
  // reachable yet" banner instead of leaving the user wondering why
  // the other side never sees anything.
  const inviteStatus = await runInviteWithStatus({
    conv: fresh,
    transport,
    beConversationId,
    conversationIdHex,
    log,
    kv,
    conversations,
    api,
    ...(peerAccountId ? { peerAccountIdOverride: peerAccountId } : {}),
    ...(preClaimedPeer ? { preClaimedPeer } : {}),
    ...(preClaimedPeers ? { preClaimedPeers } : {}),
    ...(selfSiblingDevices ? { selfSiblingDevices } : {}),
  });

  return { conv: fresh, conversationIdHex, inviteStatus };
}

async function runInviteWithStatus(params: InviteParams): Promise<InviteStatus> {
  try {
    return await invitePeerIfNeeded(params);
  } catch (err) {
    const message = (err as Error).message ?? 'unknown';
    // The wasm SDK throws `mls protocol error: Duplicate signature key
    // in proposals and group` when `addMembers` is called for a peer
    // who's already pending (a previous attempt staged the proposal
    // locally but the Commit upload to `POST /v1/messages` failed —
    // typically 400 from sender_device_mismatch). The proposal stays
    // in the SDK's pending set; a retry hits this error.
    //
    // From the orchestration's perspective the peer IS effectively
    // proposed — the SDK just hasn't been able to publish. Mark
    // invited so we don't loop calling addMembers; the user can
    // continue typing and the Commit publish will retry on the next
    // send (with sender_device alignment now in place, it'll
    // succeed). Mirrors `peer_pool_empty` handling — we surface a
    // distinct reason so the UI can show a "delivery pending" hint.
    if (message.includes('Duplicate signature key')) {
      params.log.warn({
        event: 'mlsConversation.invite.duplicate_proposal',
        beConversationId: params.beConversationId,
      });
      await markAlreadyInvited(params);
      return { kind: 'skipped', reason: 'already_invited' };
    }
    params.log.error({
      event: 'mlsConversation.invite_failed',
      beConversationId: params.beConversationId,
      error: message,
    });
    // The `consumePeerKeyPackage` BE response is 409 with the
    // marker `key_package_pool_empty`; the api layer surfaces it as
    // `NetworkError` with `message: 'network: http 409'`.
    if (message.includes('409')) {
      return { kind: 'failed', reason: 'peer_pool_empty', detail: message };
    }
    return { kind: 'failed', reason: 'other', detail: message };
  }
}

async function markAlreadyInvited(params: InviteParams): Promise<void> {
  // Persist the invited flag so the next call short-circuits before
  // calling addMembers again. Same payload shape as the success
  // path; we don't have a key_package_ref to record because the
  // retry path didn't claim a fresh KP.
  const payload = {
    peerAccountId: params.peerAccountIdOverride ?? '',
    peerDeviceId:
      params.preClaimedPeers?.[0]?.deviceIdHex ??
      params.preClaimedPeer?.deviceIdHex ??
      '',
    keyPackageRef: null as string | null,
    at: Date.now(),
    pendingPublish: true,
  };
  try {
    await params.kv.put({
      key: INVITED_KEY_PREFIX + params.beConversationId,
      value: payload,
      updatedAt: Date.now(),
    });
    if (params.conversationIdHex !== params.beConversationId) {
      await params.kv.put({
        key: INVITED_KEY_PREFIX + params.conversationIdHex,
        value: payload,
        updatedAt: Date.now(),
      });
    }
  } catch {
    // KV write failure here just means the next retry will re-hit
    // the same `Duplicate signature key` error — non-fatal.
  }
}

interface InviteParams {
  conv: MlsConversationLike;
  transport: WelcomeRecipientSink | null;
  beConversationId: string;
  conversationIdHex: string;
  log: MlsLogger;
  kv: KVStore;
  conversations: ConversationStore;
  api: ApiClient;
  peerAccountIdOverride?: string;
  // Atomic-claim result from `POST /v1/contacts/lookup`. When
  // supplied, skips both `GET /v1/users/{id}/devices` (retired) and
  // `GET /v1/users/{id}/key-package` (retired) entirely — the KP has
  // already been consumed server-side and the recipient device id
  // is the value to thread through `setWelcomeRecipient`.
  preClaimedPeer?: {
    deviceIdHex: string;
    keyPackageBytes: Uint8Array;
  };
  // Multi-device variant. Supersedes preClaimedPeer when present.
  preClaimedPeers?: ReadonlyArray<{
    deviceIdHex: string;
    keyPackageBytes: Uint8Array;
  }>;
  // The creator's OWN other devices (linked siblings, excluding this
  // device). Added in the same Commit so the 1:1 appears on them in realtime.
  selfSiblingDevices?: ReadonlyArray<{
    deviceIdHex: string;
    keyPackageBytes: Uint8Array;
  }>;
}

async function invitePeerIfNeeded(params: InviteParams): Promise<InviteStatus> {
  const {
    conv,
    transport,
    beConversationId,
    conversationIdHex,
    log,
    kv,
    conversations,
    api,
    peerAccountIdOverride,
    preClaimedPeer,
    preClaimedPeers,
    selfSiblingDevices,
  } = params;
  // Check BOTH possible invited-flag keys (beConversationId and
  // conversationIdHex) since `getOrCreateMlsConversation` writes the
  // flag under both after a successful invite. Without this, the
  // first send from WebChatRoom (which calls with beConversationId =
  // mlsIdHex) would re-enter the invite path even though
  // NewChatModal already invited under beConversationId = UUIDv5.
  const invitedKey = INVITED_KEY_PREFIX + beConversationId;
  const alreadyInvitedMain = await kv.get(invitedKey);
  if (alreadyInvitedMain?.value) {
    return { kind: 'skipped', reason: 'already_invited' };
  }
  if (conversationIdHex !== beConversationId) {
    const alt = await kv.get(INVITED_KEY_PREFIX + conversationIdHex);
    if (alt?.value) {
      return { kind: 'skipped', reason: 'already_invited' };
    }
  }

  // Resolve peer account_id. Caller-supplied override wins (used when
  // the local conversation row hasn't been written yet — eager-mint
  // path in NewChatModal). Otherwise read from the local conversations
  // store.
  let peerAccountId = peerAccountIdOverride;
  if (!peerAccountId) {
    const localConv = await conversations.get(beConversationId);
    if (!localConv) {
      // Try the conversationIdHex too, in case the row was written
      // under that key by useIncomingConversations (receiver side).
      const altConv =
        conversationIdHex !== beConversationId
          ? await conversations.get(conversationIdHex)
          : undefined;
      if (!altConv) {
        log.warn({ event: 'mlsConversation.invite.no_local_row', beConversationId });
        return { kind: 'skipped', reason: 'no_peer_account' };
      }
      if (altConv.kind !== 'direct' || !altConv.peerAccountId) {
        log.info({
          event: 'mlsConversation.invite.skipped',
          kind: altConv.kind,
          hasPeerAccountId: Boolean(altConv.peerAccountId),
        });
        return altConv.kind !== 'direct'
          ? { kind: 'skipped', reason: 'group_chat' }
          : { kind: 'skipped', reason: 'no_peer_account' };
      }
      peerAccountId = altConv.peerAccountId;
    } else if (localConv.kind !== 'direct' || !localConv.peerAccountId) {
      log.info({
        event: 'mlsConversation.invite.skipped',
        kind: localConv.kind,
        hasPeerAccountId: Boolean(localConv.peerAccountId),
      });
      return localConv.kind !== 'direct'
        ? { kind: 'skipped', reason: 'group_chat' }
        : { kind: 'skipped', reason: 'no_peer_account' };
    } else {
      peerAccountId = localConv.peerAccountId;
    }
  }

  // Resolve the peer's recipient devices + KeyPackages. Three paths:
  //   A. Multi-device pre-claimed (preClaimedPeers, plural) — every
  //      device of the peer is added to the MLS group in one
  //      addMembers call. The Welcome envelope is unicast to all
  //      devices via `POST /v1/messages?recipient=A,B,C`. Canonical
  //      per docs/architecture/multi-device.md "Per-device fan-out".
  //   B. Single-device pre-claimed (preClaimedPeer) — legacy
  //      single-device path retained so callers that haven't
  //      migrated to the multi-device API keep working. Treated
  //      internally as `preClaimedPeers = [preClaimedPeer]`.
  //   C. Legacy fan-out via `GET /v1/users/{id}/devices` +
  //      `GET /v1/users/{id}/key-package` (retired routes; kept for
  //      back-compat).
  type Resolved = { deviceIdHex: string; keyPackageBytes: Uint8Array };
  let resolvedPeers: Resolved[];
  let keyPackageRefHex: string | undefined;
  if (preClaimedPeers && preClaimedPeers.length > 0) {
    resolvedPeers = preClaimedPeers.map((p) => ({
      deviceIdHex: p.deviceIdHex,
      keyPackageBytes: p.keyPackageBytes,
    }));
  } else if (preClaimedPeer) {
    resolvedPeers = [
      {
        deviceIdHex: preClaimedPeer.deviceIdHex,
        keyPackageBytes: preClaimedPeer.keyPackageBytes,
      },
    ];
  } else {
    const devices = await fetchPeerDevices(api, peerAccountId);
    if (devices.length === 0) {
      log.warn({
        event: 'mlsConversation.invite.no_peer_devices',
        peerAccountId,
      });
      return { kind: 'failed', reason: 'no_peer_devices' };
    }
    resolvedPeers = [];
    for (const peerDevice of devices) {
      const keyPackage = await consumePeerKeyPackage(api, {
        userId: peerAccountId,
        deviceId: peerDevice.device_id,
      });
      resolvedPeers.push({
        deviceIdHex: peerDevice.device_id,
        keyPackageBytes: keyPackage.packageBytes,
      });
      // Track the first one so the invited-flag payload below has a
      // ref to surface for diagnostics. The other devices' KPs are
      // already consumed by the BE in the same loop.
      if (keyPackageRefHex === undefined) {
        keyPackageRefHex = keyPackage.keyPackageRefHex;
      }
    }
  }

  // No live transport: the Welcome envelope `addMembers()` produces
  // could not be uploaded (POST /v1/messages requires at least one
  // recipient for Welcomes), yet `addMembers` would STILL advance our
  // local MLS epoch — leaving the group desynced from the BE so every
  // later send 404s / epoch-mismatches (a silently "broken invite").
  // Bail BEFORE `addMembers` and report a retryable failure: the invited
  // flag stays unset, so the next `getOrCreateMlsConversation` call (next
  // send / reload, once the transport is restored) re-runs the invite
  // cleanly without burning the peer's KeyPackage twice.
  if (!transport) {
    log.warn({
      event: 'mlsConversation.invite.no_transport',
      beConversationId,
      peerDeviceCount: resolvedPeers.length,
    });
    return { kind: 'failed', reason: 'no_transport' };
  }
  // Fold in the creator's OWN other devices (linked siblings) so the new
  // 1:1 is delivered to every device of the account in the SAME Commit —
  // it then appears on them in realtime, not only after a later link.
  // Dedup against the peer set (siblings can never be the peer, but guard
  // against a self-claim that returned this device anyway).
  const peerDeviceIdSet = new Set(resolvedPeers.map((p) => p.deviceIdHex));
  const siblingPeers: Resolved[] = (selfSiblingDevices ?? [])
    .filter((s) => !peerDeviceIdSet.has(s.deviceIdHex))
    .map((s) => ({ deviceIdHex: s.deviceIdHex, keyPackageBytes: s.keyPackageBytes }));
  const allRecipients: Resolved[] = [...resolvedPeers, ...siblingPeers];

  // Prime the transport so the single Welcome envelope produced by
  // addMembers carries `?recipient=A,B,C` (POST /v1/messages requires
  // at least one recipient for Welcomes — see openapi.yaml). The
  // transport hint takes base64url device id *strings*.
  transport.setWelcomeRecipients(
    conversationIdHex,
    allRecipients.map((p) => p.deviceIdHex),
  );

  // `addMembers` expects `KeyPackageEntry[]` — `{ deviceId, keyPackage }`
  // objects with both fields as `Uint8Array`. The wasm SDK's serde
  // deserializer rejects raw `Uint8Array` entries with
  // `missing field deviceId`, which surfaces as a failed invite and
  // (downstream) a 400 from `POST /v1/messages` because the group
  // never got the Commit. Decode each base64url device_id string into
  // bytes for the entry. One MLS Commit advances the group from
  // 1-leaf (sender) to (1+N)-leaf in a single epoch hop.
  const entries = allRecipients.map((p) => ({
    deviceId: base64urlToBytes(p.deviceIdHex),
    keyPackage: p.keyPackageBytes,
  }));
  await conv.addMembers(entries);

  // Used by invitedPayload below and the diagnostic log at the end of
  // the function. Multi-device case: this is the *primary* (first)
  // peer device — the others are tracked implicitly via
  // `conversation_members` on the BE.
  const peerDeviceId = resolvedPeers[0]?.deviceIdHex ?? '';

  // Persist the invited flag last, so a mid-flow failure on a later
  // step can still be retried (Welcome publish, etc.). We write the
  // flag under BOTH the caller-supplied beConversationId AND the MLS
  // group's own id so a follow-up `getOrCreateMlsConversation` call
  // keyed by mlsIdHex (e.g. WebChatRoom's per-send call after
  // NewChatModal pre-minted the group) skips re-inviting and doesn't
  // burn another KeyPackage on the peer's pool.
  const invitedPayload = {
    peerAccountId,
    peerDeviceId,
    keyPackageRef: keyPackageRefHex ?? null,
    at: Date.now(),
  };
  await kv.put({
    key: invitedKey,
    value: invitedPayload,
    updatedAt: Date.now(),
  });
  if (conversationIdHex !== beConversationId) {
    await kv.put({
      key: INVITED_KEY_PREFIX + conversationIdHex,
      value: invitedPayload,
      updatedAt: Date.now(),
    });
  }

  log.info({
    event: 'mlsConversation.invite.ok',
    beConversationId,
    peerAccountId,
    peerDeviceId,
    peerDeviceCount: resolvedPeers.length,
    path: preClaimedPeers
      ? 'pre-claimed-multi'
      : preClaimedPeer
        ? 'pre-claimed'
        : 'legacy-fan-out',
  });
  return { kind: 'ok' };
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
